import {
  cancelDeleteCharacter,
  createCharacter,
  createSession,
  deleteCharacter,
  deleteCurrentSession,
  devSignIn,
  getAuthProviders,
  getCurrentSession,
  listCharacters,
  listLocalPlayers,
  newOperationId,
  redeemSignIn,
  releaseCharacter,
  selectCharacter,
  type EndpointResponse,
  type InitialState,
  type OperationId,
  type Player,
  type PlayerId,
} from '@webgame/shared';
import { ApiClient, isApiError, type ApiError } from '../api/api-client';
import { withNetworkRetries } from '../api/retry';
import { Observable, type ReadonlyObservable } from '../util/observable';
import type { TokenStore } from './token-store';

export type { InitialState } from '@webgame/shared';

export type SessionEndReason = 'replaced' | 'revoked' | 'expired' | 'outdated';

export type AuthProviders = EndpointResponse<typeof getAuthProviders>;
export type CharacterList = EndpointResponse<typeof listCharacters>;

export type SessionPhase =
  /** `error`: code of the sign-in that just failed, if any. */
  | { readonly phase: 'signed_out'; readonly error?: string }
  | { readonly phase: 'restoring' }
  /** Signed in, no character chosen: the character list (ADR 0029). */
  | { readonly phase: 'account' }
  | { readonly phase: 'active'; readonly player: Player; readonly state: InitialState }
  | { readonly phase: 'ended'; readonly reason: SessionEndReason };

/** Takes a chosen character into the game: loads it if it is not here yet, and enters. */
export type GameEntry = (player: Player) => Promise<void>;

export interface SessionClientOptions {
  readonly tokens: TokenStore;
  /** Absolute origin; omitted in the browser to use the page origin. */
  readonly httpBaseUrl?: string;
  /**
   * The game the chosen character enters (NET-CLIENT-CACHE C3): in a chunk of its own, so the
   * page shows the sign-in and the character list before any of it arrives.
   */
  readonly enter: GameEntry;
  /** The HTTP transport (tests); the page's `fetch` when omitted. */
  readonly fetch?: typeof fetch;
}

const reasonFromApiError = (error: ApiError): SessionEndReason => {
  switch (error.code) {
    case 'session_replaced':
      return 'replaced';
    case 'session_expired':
      return 'expired';
    default:
      return 'revoked';
  }
};

/**
 * The session before the game (ADR 0029, NET-CLIENT-CACHE C3): signing in, the account's
 * characters, choosing one, signing out. The page's first screens need it alone, so it carries
 * nothing of the game: the game's `GameClient` takes the session over once a character enters
 * it, and tells it when it ends. It has no UI dependencies.
 */
export class SessionClient {
  readonly api: ApiClient;
  readonly #tokens: TokenStore;
  readonly #phase = new Observable<SessionPhase>({ phase: 'signed_out' });
  readonly #enter: GameEntry;
  /** Told before the character being played is let go: signing out or the session ending. */
  readonly #leaving: ((why: 'sign_out' | 'end') => void)[] = [];

  constructor(options: SessionClientOptions) {
    this.#tokens = options.tokens;
    this.#enter = options.enter;
    this.api = new ApiClient({
      ...(options.httpBaseUrl !== undefined && { baseUrl: options.httpBaseUrl }),
      ...(options.fetch !== undefined && { fetch: options.fetch }),
      getToken: () => this.#tokens.read(),
      onUnauthorized: (error) => {
        this.end(reasonFromApiError(error));
      },
    });
  }

  get phase(): ReadonlyObservable<SessionPhase> {
    return this.#phase;
  }

  /** The tab's session token, if signed in. */
  token(): string | null {
    return this.#tokens.read();
  }

  /** Who drops the character being played when the session is let go (the game's client). */
  onLeaving(listener: (why: 'sign_out' | 'end') => void): void {
    this.#leaving.push(listener);
  }

  /** Ways of signing in this server offers. */
  authProviders(): Promise<AuthProviders> {
    return this.api.call(getAuthProviders);
  }

  /** Seeded characters for the local (development) sign-in. */
  async listPlayers(): Promise<Player[]> {
    return (await this.api.call(listLocalPlayers)).players;
  }

  async listCharacters(): Promise<CharacterList> {
    return this.api.call(listCharacters);
  }

  /**
   * Creates a character of the signed-in account, coming from `origin` (CHAR-ORIGIN; the server's
   * default when absent), skipping its prologue's memory if `skipMemory` (B165, PQ-6: only an
   * account that lived one, `canSkipMemory`); the server validates them all.
   */
  async createCharacter(name: string, origin?: string, skipMemory = false): Promise<Player> {
    const body = {
      name,
      ...(origin !== undefined && { origin }),
      ...(skipMemory && { skipMemory }),
    };
    return (await this.api.call(createCharacter, { body })).character;
  }

  /**
   * Starts deleting one of the account's characters (CHAR-DELETE, CD-1..8): unplayable from now,
   * deleted when its grace ends unless cancelled. Retried as the same operation.
   */
  async deleteCharacter(
    playerId: PlayerId,
    operationId: OperationId = newOperationId(),
  ): Promise<Player> {
    return (
      await withNetworkRetries(() =>
        this.api.call(deleteCharacter, { params: { playerId }, body: { operationId } }),
      )
    ).character;
  }

  /** Cancels a character's deletion within its grace (CD-1): playable again. */
  async cancelDeleteCharacter(playerId: PlayerId): Promise<Player> {
    return (await this.api.call(cancelDeleteCharacter, { params: { playerId } })).character;
  }

  /** Local sign-in as a seeded character (development, bots, tests). */
  async signIn(playerId: PlayerId): Promise<void> {
    const { token, player } = await this.api.call(createSession, { body: { playerId } });
    this.#tokens.write(token);
    await this.#enter(player);
  }

  /** Local sign-in as a named development identity: its account, no character chosen. */
  async signInAsDev(subject: string): Promise<void> {
    const { token } = await this.api.call(devSignIn, { body: { subject } });
    this.#tokens.write(token);
    this.#phase.set({ phase: 'account' });
  }

  /** Finishes a browser sign-in (Google): redeems the code the redirect brought back. */
  async completeBrowserSignIn(code: string): Promise<void> {
    this.#phase.set({ phase: 'restoring' });
    try {
      const { token } = await this.api.call(redeemSignIn, { body: { code } });
      this.#tokens.write(token);
      this.#phase.set({ phase: 'account' });
    } catch (error) {
      this.failSignIn(isApiError(error) ? error.code : 'unknown');
    }
  }

  /** Shows the sign-in screen with the error of a sign-in that failed. */
  failSignIn(code: string): void {
    this.#phase.set({ phase: 'signed_out', error: code });
  }

  /** Plays one of the account's characters. */
  async play(playerId: PlayerId): Promise<void> {
    const { player } = await this.api.call(selectCharacter, { body: { playerId } });
    await this.#enter(player);
  }

  /** Resumes the tab's previous session after a reload, if it is still valid. */
  async restore(): Promise<boolean> {
    if (!this.#tokens.read()) return false;
    this.#phase.set({ phase: 'restoring' });
    try {
      const { player } = await this.api.call(getCurrentSession);
      if (player) await this.#enter(player);
      else this.#phase.set({ phase: 'account' });
      return true;
    } catch (error) {
      if (isApiError(error) && error.status === 401) {
        this.#tokens.clear();
        this.#phase.set({ phase: 'signed_out' });
        return false;
      }
      this.#phase.set({ phase: 'signed_out' });
      throw error;
    }
  }

  /** The character entered the game with its initial state (the game's client says so). */
  entered(player: Player, state: InitialState): void {
    this.#phase.set({ phase: 'active', player, state });
  }

  /**
   * Back to the account's character list, the character let go (the game's client dropped what
   * it held first).
   */
  async release(): Promise<void> {
    try {
      await this.api.call(releaseCharacter);
    } catch {
      // The next `play` replaces the character anyway.
    }
    this.#phase.set({ phase: 'account' });
  }

  async signOut(): Promise<void> {
    for (const listener of this.#leaving) listener('sign_out');
    try {
      await this.api.call(deleteCurrentSession);
    } catch {
      // The session is discarded locally either way.
    }
    this.#tokens.clear();
    this.#phase.set({ phase: 'signed_out' });
  }

  /** The session ended from elsewhere (replaced, revoked, expired, an outdated page). */
  end(reason: SessionEndReason): void {
    const phase = this.#phase.get().phase;
    if (phase !== 'active' && phase !== 'restoring' && phase !== 'account') return;
    for (const listener of this.#leaving) listener('end');
    this.#tokens.clear();
    this.#phase.set({ phase: 'ended', reason });
  }

  /** Leaves the "session ended" screen. */
  acknowledgeEnd(): void {
    if (this.#phase.get().phase === 'ended') this.#phase.set({ phase: 'signed_out' });
  }
}
